import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './AddStaffForm.css';


// Use environment variables for API URLs
const API_URL = process.env.REACT_APP_API_URL;

// Configure axios for this component
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.withCredentials = true;

function AddStaffForm({ onStaffAdded, onRefresh }) {
  const [lookupMethod, setLookupMethod] = useState('single'); // 'single' or 'bulk'
  const [drivingLicenceNumber, setDrivingLicenceNumber] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [userConsent, setUserConsent] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isProcessingCSV, setIsProcessingCSV] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [csvError, setCsvError] = useState(null);


  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setCsvError(null);
    setIsProcessingCSV(true);
    setProcessedCount(0);
    setTotalCount(0);
    setLicenseData(null);

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const text = e.target.result;
        
        // More robust CSV parsing
        const licenses = text.split(/[\n\r,]/) // Split on newlines, carriage returns, or commas
          .map(line => line.trim()) // Remove whitespace
          .filter(line => line.length > 0) // Remove empty strings
          .map(line => {
            // Clean up the line - remove any non-alphanumeric characters except spaces
            const cleaned = line.replace(/[^A-Z0-9\s]/gi, '').trim();
            // Take first 16 characters and uppercase
            return cleaned.substring(0, 16).toUpperCase();
          })
          .filter(line => {
            // Skip empty lines after cleaning
            if (line.length === 0) return false;
            
            // Skip lines that are too short (less than 6 characters)
            if (line.length < 6) {
              console.log(`Skipping too short: ${line}`);
              return false;
            }

            // Validate license format - must be 6-16 alphanumeric characters
            const isValid = /^[A-Z0-9]{6,16}$/.test(line);
            if (!isValid) {
              console.log(`Invalid format: ${line}`);
              setCsvError(prev => [
                ...(prev || []),
                `Invalid license format: ${line} - Must be 6-16 alphanumeric characters`
              ]);
              return false;
            }
            return true;
          })
          .filter((license, index, array) => {
            // Remove duplicates
            return array.indexOf(license) === index;
          });

        if (licenses.length === 0) {
          setError('No valid license numbers found in file');
          setIsProcessingCSV(false);
          return;
        }

        // Debug output - show what licenses were parsed
        console.log('Parsed license numbers:', licenses);
        console.log(`Found ${licenses.length} valid license numbers`);

        setTotalCount(licenses.length);

        try {
          // Use the new bulk lookup endpoint
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('No authentication token found');
          }

          console.log(`Starting bulk lookup for ${licenses.length} licenses...`);
          
          const bulkResponse = await axios.post(
            `${API_URL}/api/staff/bulk-lookup`,
            { licenseNumbers: licenses },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          const { summary, results } = bulkResponse.data;
          setProcessedCount(summary.total);

          // Process results and set appropriate error messages
          const errors = [];
          results.forEach(result => {
            if (result.status === 'failed' || result.status === 'skipped') {
              errors.push(`${result.licenseNumber}: ${result.message}`);
            }
          });

          if (errors.length > 0) {
            setCsvError(errors);
          }

          // Show final summary
          const summaryMessage = `Successfully processed ${summary.successful} of ${summary.total} licenses` +
            (summary.failed > 0 ? `. ${summary.failed} failed` : '') +
            (summary.skipped > 0 ? `. ${summary.skipped} skipped (already exist)` : '');
          
          if (summary.failed > 0) {
            toast(summaryMessage, { 
              icon: '⚠️',
              duration: 6000,
              style: {
                background: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeeba'
              }
            });
          } else {
            toast.success(summaryMessage);
          }
          
          // Only reload if there were any successful imports
          if (summary.successful > 0) {
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          }

        } catch (err) {
          console.error('Bulk lookup error:', err);
          setIsProcessingCSV(false);
          
          // Handle specific error cases
          let errorMessage = 'Error processing bulk upload: ';
          if (err.response?.data?.error === 'Insufficient credits') {
            errorMessage = `${err.response.data.message}`;
            toast.error(errorMessage);
          } else if (err.response?.data?.error?.includes('Invalid license format')) {
            errorMessage += 'Some license numbers have invalid format';
            setCsvError(err.response.data.invalidLicenses || []);
          } else {
            errorMessage += err.response?.data?.message || err.message || 'Unknown error occurred';
          }
          
          setError(errorMessage);
          return;
        }
        
        setIsProcessingCSV(false);
      };

      reader.readAsText(file);
    } catch (err) {
      setError(`Error processing CSV file: ${err.message}`);
      setIsProcessingCSV(false);
    }
  };

  const handleLicenseLookup = async (drivingLicenceNumber) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // First request - lookup
      console.log('Making lookup request...');
      const lookupResponse = await axios.post(
        `${API_URL}/api/staff/lookup`,
        { drivingLicenceNumber },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!lookupResponse.data || (!lookupResponse.data.staff && !lookupResponse.data.dvlaResponse)) {
        throw new Error('Invalid response format from server');
      }

      const staffData = lookupResponse.data.staff;
      const dvlaResponse = lookupResponse.data.dvlaResponse;
      
      // Log the raw response data
      console.log('Raw DVLA Response:', dvlaResponse);
      console.log('Response Data Object:', dvlaResponse.response_data);

      // Try to get common_entitlement_expiry from vehicle_categories
      let commonEntitlementExpiry = null;
      if (dvlaResponse.vehicle_categories && dvlaResponse.vehicle_categories.length > 0) {
        // Get the latest expiry date from vehicle categories
        const expiryDates = dvlaResponse.vehicle_categories
          .map(cat => cat.validUntil)
          .filter(date => date); // Remove null/undefined dates
        
        if (expiryDates.length > 0) {
          // Sort dates in descending order and take the latest
          commonEntitlementExpiry = new Date(Math.max(...expiryDates.map(d => new Date(d))))
            .toISOString()
            .split('T')[0];
        }
      }

      const latestCheck = {
        status: dvlaResponse?.status || 'UNKNOWN',
        points_total: dvlaResponse?.points_total || 0,
        license_valid_from: dvlaResponse?.license_valid_from,
        license_valid_to: dvlaResponse?.expiry_date,
        license_type: dvlaResponse?.license_type,
        check_date: new Date().toISOString(),
        common_entitlement_expiry: commonEntitlementExpiry,
        response_data: dvlaResponse
      };

      console.log('Latest Check Object with common_entitlement_expiry:', latestCheck);

      const combinedData = {
        ...staffData,
        status: latestCheck.status,
        points_total: latestCheck.points_total,
        license_valid_from: latestCheck.license_valid_from,
        expiry_date: latestCheck.license_valid_to,
        license_type: latestCheck.license_type,
        common_entitlement_expiry: commonEntitlementExpiry,
        vehicle_categories: dvlaResponse?.vehicle_categories || [],
        endorsements: dvlaResponse?.endorsements || [],
        last_checked: latestCheck.check_date,
        licenseChecks: [latestCheck]
      };

      console.log('Combined Data Object with common_entitlement_expiry:', combinedData);

      setLicenseData(combinedData);
      return combinedData;

    } catch (error) {
      console.error('Complete error details:', error);
      if (error.response) {
        console.error('Server error response:', error.response.data);
        
        // Use the detailed message from the server if available
        if (error.response.data.message) {
          throw new Error(error.response.data.message);
        } else {
          throw new Error(error.response.data.error || 'Server error');
        }
      } else if (error.request) {
        throw new Error('Network error - no response received');
      } else {
        throw new Error(error.message || 'Error processing license lookup');
      }
    } finally {
      setLoading(false);
    }
  };

  const showSuccessNotification = () => {
    toast.success('License check completed successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setLicenseData(null);

    try {
      const dvlaData = await handleLicenseLookup(drivingLicenceNumber);
      console.log('Complete DVLA Data structure:', dvlaData);
      setLicenseData(dvlaData);
      setDrivingLicenceNumber('');
      showSuccessNotification();
    } catch (err) {
      console.error('Submit error details:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setIsCompleting(true);
    setTimeout(() => {
      setIsCompleting(false);
      setLicenseData(null);
      if (onStaffAdded) {
        onStaffAdded();
      }
      window.location.reload();
    }, 3000);
  };

  const handleViewDocument = (e) => {
    e.preventDefault();
    window.open(process.env.PUBLIC_URL + '/DVLAConsent.pdf', '_blank');
  };



  return (
    <div className="license-lookup-container">
      <h2>DVLA License Lookup</h2>
      
      {/* Lookup Method Selection */}
      <div className="lookup-methods">
        <div 
          className={`lookup-method ${lookupMethod === 'single' ? 'active' : ''}`}
          onClick={() => setLookupMethod('single')}
        >
          <h3>
            <span className="icon">👤</span>
            Single License Lookup
          </h3>
          <p>Check a single driving license number</p>
        </div>
        
        <div 
          className={`lookup-method ${lookupMethod === 'bulk' ? 'active' : ''}`}
          onClick={() => setLookupMethod('bulk')}
        >
          <h3>
            <span className="icon">📋</span>
            Bulk License Lookup
          </h3>
          <p>Upload a CSV file with multiple license numbers</p>
        </div>
      </div>

      {/* Consent Checkbox */}
      <div className="consent-group">
        <label>
          <input
            type="checkbox"
            checked={userConsent}
            onChange={(e) => setUserConsent(e.target.checked)}
          />
          <span>We confirm we are in receipt of signed D906/ADD form(s) for the licence(s) we are submitting</span>
        </label>
        <button 
          onClick={handleViewDocument}
          className="view-consent-doc"
          type="button"
        >
          View Consent Form
        </button>
      </div>

      {/* Lookup Content */}
      <div className="lookup-content">
        {lookupMethod === 'single' ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                value={drivingLicenceNumber}
                onChange={(e) => setDrivingLicenceNumber(e.target.value.toUpperCase())}
                placeholder="Enter Driving License Number"
                disabled={!userConsent || loading}
                required
              />
              <div className="helper-text">
                UK DVLA license numbers are typically 16 characters long, starting with 5 letters followed by 7 numbers and ending with 4 letters/numbers (e.g. MORGA712054SM9J)
              </div>
            </div>
            <button 
              type="submit" 
              disabled={loading || !userConsent}
              className="check-license-btn"
            >
              {loading ? (
                <span className="loading-spinner">Loading...</span>
              ) : (
                'Check License'
              )}
            </button>
          </form>
        ) : (
          <div className="file-upload-area">
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleCSVUpload}
              disabled={!userConsent || isProcessingCSV}
              id="csv-upload"
              style={{ display: 'none' }}
            />
            <label 
              htmlFor="csv-upload" 
              className={!userConsent || isProcessingCSV ? 'disabled' : ''}
              onClick={(e) => {
                if (!userConsent || isProcessingCSV) {
                  e.preventDefault();
                  if (!userConsent) {
                    setError('Please confirm receipt of signed D906/ADD form(s) before uploading');
                  }
                }
              }}
            >
              <div className="icon">📤</div>
              <p>
                {isProcessingCSV 
                  ? 'Processing...' 
                  : 'Click or drag file to upload CSV'
                }
              </p>
              <span className="hint">Supported formats: .csv, .txt</span>
            </label>
          </div>
        )}

        {/* Progress Bar */}
        {isProcessingCSV && (
          <div className={`progress-container ${processedCount === totalCount ? 'hide' : ''}`}>
            <div className="progress-text">
              Processing {processedCount} of {totalCount} licenses
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${totalCount > 0 ? (processedCount / totalCount) * 100 : 0}%` 
                }}
              />
            </div>
          </div>
        )}

        {/* Error Messages */}
        {error && <div className="error-message">{error}</div>}
        {csvError && csvError.length > 0 && (
          <div className="csv-errors">
            <h4>Processing Errors:</h4>
            <div className="error-list">
              {csvError.map((error, index) => (
                <div key={index} className="error-item">
                  <span className="error-icon">⚠️</span>
                  <span className="error-text">{error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>



      {/* License Details Section - Keep existing code */}
      {licenseData && (
        <div className="license-details">
          <h3>License Details</h3>
          
          <div className="info-sections-wrapper">
            {/* Driver Information */}
            <div className="info-section">
              <h4>Driver Information</h4>
              <div className="detail-item">
                <strong>First Name:</strong> {
                  licenseData.first_name || 
                  licenseData.driver?.name?.split(' ').slice(0, -1).join(' ') || 
                  'Not provided'
                }
              </div>
              <div className="detail-item">
                <strong>Last Name:</strong> {
                  licenseData.last_name || 
                  licenseData.driver?.name?.split(' ').slice(-1)[0] || 
                  'Not provided'
                }
              </div>
              <div className="detail-item">
                <strong>Date of Birth:</strong> {
                  formatDate(licenseData.date_of_birth) || 
                  formatDate(licenseData.driver?.dob) || 
                  'Not provided'
                }
              </div>
              <div className="detail-item">
                <strong>Gender:</strong> {
                  licenseData.gender || 
                  licenseData.driver?.gender || 
                  'Not specified'
                }
              </div>
              <div className="detail-item">
                <strong>License Number:</strong> {
                  licenseData.driving_license_number || 
                  licenseData.driver?.licenseNumber || 
                  'Not provided'
                }
              </div>
            </div>

            {/* License Status Information */}
            <div className="info-section">
              <h4>License Status</h4>
              <div className="detail-item">
                <strong>Status:</strong> 
                <span className={`status ${(licenseData.status || '').toLowerCase()}`}>
                  {licenseData.status || 'Unknown'}
                </span>
              </div>
              <div className="detail-item">
                <strong>Type:</strong> {licenseData.license_type || 'Unknown'}
              </div>
              <div className="detail-item">
                <strong>Valid From:</strong> {licenseData.license_valid_from || 'Not provided'}
              </div>
              <div className="detail-item">
                <strong>Expiry Date:</strong> {licenseData.expiry_date || 'Not provided'}
              </div>
              <div className="detail-item">
                <strong>Common Entitlement Expiry:</strong> {
                  licenseData.common_entitlement_expiry 
                    ? new Date(licenseData.common_entitlement_expiry).toLocaleDateString()
                    : 'Not provided'
                }
              </div>
              <div className="detail-item">
                <strong>Points Total:</strong> 
                <span className={`points ${licenseData.points_total > 6 ? 'high' : licenseData.points_total > 3 ? 'medium' : 'low'}`}>
                  {licenseData.points_total || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Address Information */}
          {(() => {
            const addressFields = [
              licenseData.address_line1,
              licenseData.address_line2,
              licenseData.address_line3,
              licenseData.address_line4,
              licenseData.address_line5,
              licenseData.postcode,
              licenseData.address
            ];
            
            const hasAnyAddress = addressFields.some(field => field && field !== 'Not provided');
            
            if (!hasAnyAddress) return null;
            
            return (
              <div className="info-section">
                <h4>Address Information</h4>
                {licenseData.address_line1 && licenseData.address_line1 !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Address Line 1:</strong> {licenseData.address_line1}
                  </div>
                )}
                {licenseData.address_line2 && licenseData.address_line2 !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Address Line 2:</strong> {licenseData.address_line2}
                  </div>
                )}
                {licenseData.address_line3 && licenseData.address_line3 !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Address Line 3:</strong> {licenseData.address_line3}
                  </div>
                )}
                {licenseData.address_line4 && licenseData.address_line4 !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Address Line 4:</strong> {licenseData.address_line4}
                  </div>
                )}
                {licenseData.address_line5 && licenseData.address_line5 !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Address Line 5:</strong> {licenseData.address_line5}
                  </div>
                )}
                {licenseData.postcode && licenseData.postcode !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Postcode:</strong> {licenseData.postcode}
                  </div>
                )}
                {licenseData.address && licenseData.address !== 'Not provided' && (
                  <div className="detail-item">
                    <strong>Full Address:</strong> {licenseData.address}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Vehicle Categories */}
          {licenseData.vehicle_categories && licenseData.vehicle_categories.length > 0 && (
            <div className="vehicle-categories">
              <h4>Vehicle Categories</h4>
              <table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Valid Until</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {licenseData.vehicle_categories.map((category, index) => (
                    <tr key={index}>
                      <td>{category.code}</td>
                      <td>{category.type}</td>
                      <td>{category.validUntil || 'Not specified'}</td>
                      <td>
                        <div title={category.legal_literal} style={{ cursor: 'help' }}>
                          {category.legal_literal?.length > 100 
                            ? `${category.legal_literal.substring(0, 100)}...` 
                            : category.legal_literal}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Endorsements */}
          {licenseData.endorsements && licenseData.endorsements.length > 0 && (
            <div className="endorsements">
              <h4>Endorsements</h4>
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Points</th>
                    <th>Offense Date</th>
                    <th>Conviction Date</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {licenseData.endorsements.map((endorsement, index) => (
                    <tr key={index}>
                      <td>{endorsement.offenceCode}</td>
                      <td>{endorsement.penaltyPoints}</td>
                      <td>{endorsement.offenceDate}</td>
                      <td>{endorsement.convictionDate}</td>
                      <td>{endorsement.offenceLegalLiteral}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="complete-section">
            <button 
              onClick={handleComplete}
              className={`complete-btn ${isCompleting ? 'success' : ''}`}
              disabled={isCompleting}
            >
              {isCompleting ? 'Completed ✓' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddStaffForm; 